import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import BorderColor from "%/dynamic-component-editor/options/border-color";
import BorderWidth from "%/dynamic-component-editor/options/border-width";
import BorderRadius from "%/dynamic-component-editor/options/border-radius";
import FontColor from "%/dynamic-component-editor/options/font-color";
import FontFamily from "%/dynamic-component-editor/options/font-family";
import FontSize from "%/dynamic-component-editor/options/font-size";
import normalizeFontSizeStyleValue from "%/dynamic-component-editor/helpers/font-size-style-value";
import FontWeight from "%/dynamic-component-editor/options/font-weight";
import Margins from "%/dynamic-component-editor/options/margins";
import Padding from "%/dynamic-component-editor/options/padding";
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import SeedField from "%/dynamic-component-editor/options/seed-field";
import { DynamicAppInputFocusState } from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/dynamic-app-input-states";

// Resolution-only mixin for the DynamicApp*InputElements: option/bundle
// DECLARATIONS must live on each component or they drop from the release.
export default {
  computed: {
    // A shipped input carries its seed in options.context.seedField, written by
    // the installer from layout.json's `seed-field`. A hand-built one has no
    // installer behind it, so the option is the only way to say which stored
    // field the draft starts from -- without it a rebuilt card opens empty
    // where the shipped one shows the saved answer.
    appInputSeedField() {
      return this.elementContext?.seedField
        || this.getOptionValue(SeedField, BaseOption.$states.DEFAULT, null)
        || "";
    },
    appInputSeededValue() {
      const seeded = this.siblingFieldValue(this.appInputSeedField);
      return seeded === null || seeded === undefined ? "" : seeded;
    },
    // Every resolver is presence-gated: an untouched option must stay off the
    // style-overrides, or the option-class default clobbers the themed styleGuide.
    appInputMargins() {
      if (!this.appInputOptionIsSet(this.getOptionKey(Margins))) {
        return {};
      }
      const margins = this.getOptionValue(Margins, BaseOption.$states.DEFAULT, null) ?? {};
      const overrides = {};
      Object.entries({
        marginTop: margins.top,
        marginBottom: margins.bottom,
        marginLeft: margins.left,
        marginRight: margins.right,
      }).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          overrides[key] = value;
        }
      });
      return overrides;
    },
    appInputPadding() {
      if (!this.appInputOptionIsSet(this.getOptionKey(Padding))) {
        return {};
      }
      const padding = this.getOptionValue(Padding, BaseOption.$states.DEFAULT, null) ?? {};
      const overrides = {};
      Object.entries(padding).forEach(([side, value]) => {
        if (value !== null && value !== undefined) {
          overrides[side] = value;
        }
      });
      return overrides;
    },
    appInputBackgroundColor() {
      return this.appInputValueIfSet(BackgroundColor);
    },
    appInputBorderColor() {
      return this.appInputValueIfSet(BorderColor);
    },
    appInputBorderWidth() {
      return this.appInputValueIfSet(BorderWidth);
    },
    appInputBorderRadius() {
      return this.appInputValueIfSet(BorderRadius);
    },
    appInputFocusBorderColor() {
      return this.appInputFocusValueIfSet(BorderColor);
    },
    appInputFocusBorderWidth() {
      return this.appInputFocusValueIfSet(BorderWidth);
    },
    appInputFocusBorderRadius() {
      return this.appInputFocusValueIfSet(BorderRadius);
    },
    appInputFontColor() {
      return this.appInputValueIfSet(FontColor);
    },
    appInputFontFamily() {
      return this.appInputValueIfSet(FontFamily);
    },
    appInputFontSize() {
      const value = this.appInputValueIfSet(FontSize);
      return value === null ? null : normalizeFontSizeStyleValue(value, { element: "AppInput", option: "font-size" });
    },
    appInputFontWeight() {
      return this.appInputValueIfSet(FontWeight);
    },
    // For inner components with no styleGuide surface (file/image/video inputs). A styled
    // inner component (the story input's iEditor) must take these itself, or they land outside its border.
    appInputWrapperStyleOverrides() {
      const padding = this.appInputPadding;
      return this.appInputStyleOverrides({
        paddingTop: padding.top ?? null,
        paddingBottom: padding.bottom ?? null,
        paddingLeft: padding.left ?? null,
        paddingRight: padding.right ?? null,
        backgroundColor: this.appInputBackgroundColor,
        borderColor: this.appInputBorderColor,
        borderWidth: this.appInputBorderWidth,
        borderRadius: this.appInputBorderRadius,
      });
    },
  },
  methods: {
    // True only when the key exists in the element's actual options tree
    // (release blob in production, live DynamicElement tree in the editor).
    appInputOptionIsSet(optionKey) {
      const trees = [];
      if (this.optionValues && Object.keys(this.optionValues).length > 0) {
        trees.push(this.optionValues);
      }
      if (this.dynamicElement?.parsedOptions) {
        trees.push(this.dynamicElement.parsedOptions);
      }
      return trees.some(tree => ["light-mode", "dark-mode"].some(mode => {
        const modeTree = tree?.[mode];
        if (!modeTree || typeof modeTree !== "object") {
          return false;
        }
        return Object.values(modeTree).some(breakpointTree => breakpointTree && typeof breakpointTree === "object"
          && Object.values(breakpointTree).some(stateTree => stateTree && typeof stateTree === "object"
            && Object.prototype.hasOwnProperty.call(stateTree, optionKey)));
      }));
    },
    appInputValueIfSet(OptionClass) {
      if (!this.appInputOptionIsSet(this.getOptionKey(OptionClass))) {
        return null;
      }
      return this.appInputResolvedValue(this.getOptionValue(OptionClass));
    },
    appInputFocusValueIfSet(OptionClass) {
      if (!this.appInputOptionIsSet(this.getOptionKey(OptionClass))) {
        return null;
      }
      return this.appInputResolvedValue(this.getOptionValueAtState(OptionClass, DynamicAppInputFocusState));
    },
    // Colors persist as {value, source} wrappers; VCL resolves the bare
    // token, so unwrap to the string (engine unwrapRawLeaf convention).
    appInputResolvedValue(value) {
      let unwrapped = value;
      if (value && typeof value === "object" && "value" in value) {
        unwrapped = value.value;
      }
      if (unwrapped === undefined || unwrapped === null || unwrapped === "" || unwrapped === "unset" || unwrapped === "none") {
        return null;
      }
      return unwrapped;
    },
    appInputStyleOverrides(pairs) {
      const overrides = { ...this.appInputMargins };
      Object.entries(pairs).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          overrides[key] = value;
        }
      });
      return overrides;
    },
  },
};
